import {
  Autocomplete,
  Box,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import { InfoModal } from '@/commons/info-modal';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { CreateAdFormData } from '@/pages/ad-pages/CreateAd';
import { Card } from '@/features/card';
import { useGetAllCampaignsQuery } from '@/models/campaign';

type Props = {
  control: Control<CreateAdFormData>;
  errors: FieldErrors<CreateAdFormData>;
};

const isCampaignObject = (value: unknown): value is { id: string | null; title: string } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value
  );
};

export const CampaignSection: FC<Props> = ({
  control,
  errors,
}) => {
  console.log('🚀 ~ errors:', errors)
  const { t } = useTranslation();
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const { data: campaigns } = useGetAllCampaignsQuery();
  const [inputValue, setInputValue] = useState('');

  return (
    <section className="flex-1">
      <Typography variant="h6" sx={{ mb: '8px' }}>
        <Trans i18nKey="campaign_section.title" />
        <IconButton size="medium" onClick={() => setInfoModalOpen(true)}>
          <InfoIcon fontSize="inherit" />
        </IconButton>
      </Typography>

      <Card>
        <div className="mt-1 flex gap-2">
            <Controller
              name="campaign"
              control={control}
              defaultValue={{ id: null, title: '' }}
              render={({ field: { onChange, value } }) => {
                const selectedCampaign = campaigns?.find((it) => it.id === value.id)
                const showNewCampaignMessage = inputValue && !selectedCampaign;

                return (
                  <Autocomplete
                    id="campaign"
                    freeSolo
                    className="md:flex-1"
                    value={selectedCampaign || null}
                    onChange={(_, value) => {
                      if (isCampaignObject(value)) {
                        onChange({ id: value.id, title: value.title });
                      } else {
                        onChange({ id: null, title: value as string });
                      }
                    }}
                    inputValue={inputValue}
                    onInputChange={(_, newInputValue) => {
                      setInputValue(newInputValue);
                      if (!campaigns?.find(c => c.title === newInputValue)) {
                        onChange({ id: null, title: newInputValue });
                      }
                    }}
                    options={campaigns || []}
                    getOptionLabel={(option) => {
                      if (typeof option === 'string') {
                        return option;
                      }
                      return option.title;
                    }}
                    renderOption={(props, option) => {
                      const { key, ...otherProps } = props;
                      return (
                        <Box component="li" key={key} {...otherProps}>
                          {option.title}
                        </Box>
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('campaign_section.autocomplete.label')}
                        helperText={showNewCampaignMessage ? t('campaign_section.new_campaign_message') : ''}
                      />
                    )}
                  />
                );
              }}
            />
        </div>
      </Card>

      <InfoModal
        title={t('basic_info_section.title')}
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
      >
        <Trans i18nKey="basic_info_section.info" />
      </InfoModal>
    </section>
  );
};
